import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Coins, ShoppingCart, History, Gift, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import './CreditBadge.css';

function CreditBadge() {
  const { t } = useTranslation();
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCredits();
  }, []);

  const fetchCredits = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/credits/balance`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCredits(data);
        
        // Show toast if daily bonus was awarded
        if (data.daily_bonus_awarded && data.daily_bonus_awarded > 0) {
          toast.success(t('credits.dailyBonus', { amount: data.daily_bonus_awarded }), {
            duration: 5000,
            position: 'top-center',
          });
        }
      }
    } catch (error) {
      console.error('Error fetching credits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = () => {
    setShowMenu(false);
    navigate('/coins');
  };

  const handleHistory = () => {
    setShowMenu(false);
    navigate('/credit-history');
  };

  if (loading || !credits) {
    return null;
  }

  return (
    <>
      <div className="credit-badge-container">
        <div 
          className="credit-badge" 
          data-testid="credit-badge"
          onClick={() => setShowMenu(!showMenu)}
          style={{ cursor: 'pointer' }}
          title={t('credits.showDetails')}
        >
          <Coins size={18} className="credit-icon" />
          <span className="credit-amount">{credits.coins?.toFixed(2) || '0.00'}</span>
          <span className="credit-label">Coins</span>
        </div>

        {showMenu && (
          <>
            <div className="credit-menu-overlay" onClick={() => setShowMenu(false)}></div>
            <div className="credit-menu" data-testid="credit-menu">
              <div className="credit-breakdown-section">
                <div className="credit-breakdown-title">{t('credits.availableCredits')}</div>
                
                <div className="credit-type-item" data-testid="daily-credits-info">
                  <div className="credit-type-left">
                    <Gift size={16} className="credit-type-icon" />
                    <span className="credit-type-name">{t('credits.daily')}</span>
                  </div>
                  <span className="credit-type-amount">
                    {credits.daily_credits?.toFixed(2) || '0.00'} / 5
                  </span>
                </div>

                <div className="credit-type-item" data-testid="monthly-credits-info">
                  <div className="credit-type-left">
                    <RefreshCw size={16} className="credit-type-icon" />
                    <span className="credit-type-name">
                      {credits.subscription_type === 'trial' ? t('credits.oneTime') : t('credits.monthly')}
                    </span>
                  </div>
                  <span className="credit-type-amount">
                    {credits.monthly_credits?.toFixed(2) || '0.00'} / {credits.monthly_limit || 0}
                  </span>
                </div>

                <div className="credit-type-item" data-testid="permanent-credits-info">
                  <div className="credit-type-left">
                    <Sparkles size={16} className="credit-type-icon" />
                    <span className="credit-type-name">{t('credits.permanent')}</span>
                  </div>
                  <span className="credit-type-amount">
                    {credits.permanent_credits?.toFixed(2) || '0.00'}
                  </span>
                </div>
              </div>

              <div className="credit-menu-divider"></div>

              <button 
                className="credit-menu-item" 
                onClick={handlePurchase}
                data-testid="menu-purchase-coins"
              >
                <ShoppingCart size={18} />
                <span>{t('credits.buyCoins')}</span>
              </button>
              <button 
                className="credit-menu-item" 
                onClick={handleHistory}
                data-testid="menu-credit-history"
              >
                <History size={18} />
                <span>{t('credits.coinHistory')}</span>
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default CreditBadge;

